/**
 * service-delivery client. Mirrors the controllers under
 * `apps/service-delivery/src/modules/{route,driver,vehicle,pod,shift}/`.
 *
 * The controllers expose, at base `http://localhost:3080`:
 *   - GET    /v1/routes                           — list routes (paginated)
 *   - POST   /v1/routes                           — create draft route
 *   - GET    /v1/routes/:id                       — fetch with stops
 *   - POST   /v1/routes/:id/dispatch              — dispatch to driver
 *   - POST   /v1/routes/:id/start|complete|reconcile
 *   - POST   /v1/routes/:id/stops/:stopId/{arrive,complete,fail}
 *   - GET    /v1/drivers                          — list drivers
 *   - POST   /v1/drivers                          — create
 *   - GET    /v1/drivers/:id                      — fetch
 *   - POST   /v1/drivers/:id/{status,vehicle}     — mutations
 *   - GET    /v1/vehicles                         — list vehicles
 *   - POST   /v1/vehicles                         — create
 *   - GET    /v1/vehicles/:id                     — fetch
 *   - POST   /v1/vehicles/:id/status              — change status
 *   - GET    /v1/pods/queue                       — review queue
 *   - GET    /v1/pods/:id                         — fetch a POD
 *   - POST   /v1/pods/:id/review                  — approve/reject
 *
 * Endpoints shipped in slice 2:
 *   - GET /v1/delivery/dashboard  — aggregate counts by status
 *   - GET /v1/delivery/exceptions — failed-stop feed
 *   - POST /v1/delivery/exceptions/:id/resolve — REATTEMPT / RETURN_TO_SENDER / ESCALATE
 *
 * Endpoints NOT (yet) shipped by service-delivery:
 *   - GET /v1/delivery/disputes / performance
 *
 * RBAC: every method requires a `Delivery/<verb>` permission. The dev-paste
 * JWT 401/403s without it; pages render the deferred-permission alert.
 */
import { env } from '../env';

import { request } from './client';

// --------------------------------------------------------------------------
//  Routes
// --------------------------------------------------------------------------

export type RouteStatus = 'DRAFT' | 'DISPATCHED' | 'IN_PROGRESS' | 'COMPLETED' | 'RECONCILED';
export type StopStatus = 'PENDING' | 'ARRIVED' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
export type StopKind = 'PICKUP' | 'DELIVERY';
export type StopFailureReason =
  | 'RECIPIENT_ABSENT'
  | 'REFUSED'
  | 'ADDRESS_INVALID'
  | 'INACCESSIBLE'
  | 'OTHER';

export interface RouteStop {
  readonly id: string;
  readonly routeId: string;
  readonly sequence: number;
  readonly kind: StopKind;
  readonly mailItemId: string | null;
  readonly address: unknown;
  readonly contactName: string | null;
  readonly contactPhone: string | null;
  readonly status: StopStatus;
  readonly arrivedAt: string | null;
  readonly completedAt: string | null;
  readonly failureReason: StopFailureReason | null;
  readonly podRecordId: string | null;
  /** Stop latitude as a decimal-degree string, or null when the upstream
   *  address has no geo data. Paired with `lng`. */
  readonly lat: string | null;
  /** Stop longitude as a decimal-degree string. */
  readonly lng: string | null;
}

export interface DeliveryRoute {
  readonly id: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly driverId: string | null;
  readonly vehicleId: string | null;
  readonly shiftId: string | null;
  readonly routeCode: string;
  readonly status: RouteStatus;
  readonly scheduledStart: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly stopCount: number;
  readonly estimatedDurationMinutes: number | null;
  readonly stops?: readonly RouteStop[];
}

export interface ListRoutesQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: RouteStatus;
  readonly driverId?: string;
}

export interface ListRoutesResponse {
  readonly items: readonly DeliveryRoute[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface CreateRouteRequest {
  readonly scheduledStart: string;
  readonly driverId?: string;
  readonly vehicleId?: string;
  readonly estimatedDurationMinutes?: number;
  readonly stops: readonly {
    readonly sequence: number;
    readonly kind: StopKind;
    readonly mailItemId?: string;
    readonly address: Record<string, unknown>;
    readonly contactName?: string;
    readonly contactPhone?: string;
  }[];
}

export interface DispatchRouteRequest {
  readonly driverId: string;
  readonly vehicleId?: string;
}

const baseUrl = (): string => env.serviceDeliveryBaseUrl;

export async function listRoutes(query: ListRoutesQuery = {}): Promise<ListRoutesResponse> {
  return request<ListRoutesResponse>(baseUrl(), '/v1/routes', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
      driverId: query.driverId,
    },
  });
}

export async function getRoute(id: string): Promise<DeliveryRoute> {
  return request<DeliveryRoute>(baseUrl(), `/v1/routes/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export async function createRoute(body: CreateRouteRequest): Promise<DeliveryRoute> {
  return request<DeliveryRoute>(baseUrl(), '/v1/routes', { method: 'POST', body });
}

// --------------------------------------------------------------------------
//  Auto-build routes (POST /v1/routes/auto-build)
//
//  Cluster open OUT_FOR_DELIVERY mail items into draft routes in one click.
//  Request fields mirror the locked DTO on the service-delivery side:
//   - scheduledStart  ISO datetime, required
//   - maxStopsPerRoute  optional, default 25 (server-side), 1..100
//   - groupBy         'destState' (default) or 'destLga'
//   - serviceClasses  optional whitelist
//   - dryRun          when true, the server returns the cluster preview with
//                     empty routeId/routeCode strings and DOES NOT persist
// --------------------------------------------------------------------------

export interface AutoBuildRoutesRequest {
  readonly scheduledStart: string;
  readonly maxStopsPerRoute?: number;
  readonly groupBy?: 'destState' | 'destLga';
  readonly serviceClasses?: readonly string[];
  readonly dryRun?: boolean;
}

export interface AutoBuildRouteSummary {
  readonly routeId: string;
  readonly routeCode: string;
  readonly stopCount: number;
  readonly groupKey: string;
  readonly serviceClass: string;
  readonly estimatedDurationMinutes: number;
}

export interface AutoBuildRoutesResponse {
  readonly created: readonly AutoBuildRouteSummary[];
  readonly skipped: {
    readonly alreadyOnOpenRoute: number;
    readonly noAddress: number;
    readonly unknownServiceClass: number;
  };
  readonly totalConsidered: number;
  readonly dryRun: boolean;
}

export async function autoBuildRoutes(
  body: AutoBuildRoutesRequest,
): Promise<AutoBuildRoutesResponse> {
  return request<AutoBuildRoutesResponse>(baseUrl(), '/v1/routes/auto-build', {
    method: 'POST',
    body,
  });
}

export async function dispatchRoute(
  id: string,
  body: DispatchRouteRequest,
): Promise<DeliveryRoute> {
  return request<DeliveryRoute>(
    baseUrl(),
    `/v1/routes/${encodeURIComponent(id)}/dispatch`,
    { method: 'POST', body },
  );
}

// --------------------------------------------------------------------------
//  Drivers
// --------------------------------------------------------------------------

export type DriverStatus = 'ACTIVE' | 'SUSPENDED' | 'TERMINATED';

export interface Driver {
  readonly id: string;
  readonly driverNumber: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly keycloakSub: string | null;
  readonly fullName: string;
  readonly primaryPhone: string;
  readonly licenseNumber: string | null;
  readonly licenseExpiresAt: string | null;
  readonly assignedVehicleId: string | null;
  readonly activeShiftId: string | null;
  readonly status: DriverStatus;
  readonly hiredAt: string;
  readonly terminatedAt: string | null;
}

export interface ListDriversQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: DriverStatus;
}

export interface ListDriversResponse {
  readonly items: readonly Driver[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface CreateDriverRequest {
  readonly fullName: string;
  readonly primaryPhone: string;
  readonly licenseNumber?: string;
  readonly licenseExpiresAt?: string;
  readonly keycloakSub?: string;
}

export async function listDrivers(query: ListDriversQuery = {}): Promise<ListDriversResponse> {
  return request<ListDriversResponse>(baseUrl(), '/v1/drivers', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
    },
  });
}

export async function createDriver(body: CreateDriverRequest): Promise<Driver> {
  return request<Driver>(baseUrl(), '/v1/drivers', { method: 'POST', body });
}

export async function getDriver(id: string): Promise<Driver> {
  return request<Driver>(baseUrl(), `/v1/drivers/${encodeURIComponent(id)}`, { method: 'GET' });
}

export async function updateDriverStatus(
  id: string,
  status: DriverStatus,
  reason?: string,
): Promise<Driver> {
  const body = reason !== undefined ? { status, reason } : { status };
  return request<Driver>(baseUrl(), `/v1/drivers/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body,
  });
}

export async function assignVehicleToDriver(id: string, vehicleId: string): Promise<Driver> {
  return request<Driver>(baseUrl(), `/v1/drivers/${encodeURIComponent(id)}/vehicle`, {
    method: 'POST',
    body: { vehicleId },
  });
}

// --------------------------------------------------------------------------
//  Vehicles
// --------------------------------------------------------------------------

export type VehicleKind = 'VAN' | 'MOTORBIKE' | 'BICYCLE' | 'TRUCK';
export type VehicleStatus = 'ACTIVE' | 'MAINTENANCE' | 'RETIRED';

export interface Vehicle {
  readonly id: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly registration: string;
  readonly kind: VehicleKind;
  readonly make: string;
  readonly model: string;
  readonly capacityKg: number;
  readonly status: VehicleStatus;
  readonly active: boolean;
}

export interface ListVehiclesQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: VehicleStatus;
  readonly kind?: VehicleKind;
}

export interface ListVehiclesResponse {
  readonly items: readonly Vehicle[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface CreateVehicleRequest {
  readonly registration: string;
  readonly kind: VehicleKind;
  readonly make: string;
  readonly model: string;
  readonly capacityKg: number;
}

export async function listVehicles(query: ListVehiclesQuery = {}): Promise<ListVehiclesResponse> {
  return request<ListVehiclesResponse>(baseUrl(), '/v1/vehicles', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
      kind: query.kind,
    },
  });
}

export async function getVehicle(id: string): Promise<Vehicle> {
  return request<Vehicle>(baseUrl(), `/v1/vehicles/${encodeURIComponent(id)}`, { method: 'GET' });
}

export async function createVehicle(body: CreateVehicleRequest): Promise<Vehicle> {
  return request<Vehicle>(baseUrl(), '/v1/vehicles', { method: 'POST', body });
}

export async function setVehicleStatus(id: string, status: VehicleStatus): Promise<Vehicle> {
  return request<Vehicle>(baseUrl(), `/v1/vehicles/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body: { status },
  });
}

// --------------------------------------------------------------------------
//  POD review
// --------------------------------------------------------------------------

export type PodReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface PodRecord {
  readonly id: string;
  readonly mailItemId: string;
  readonly routeStopId: string | null;
  readonly driverId: string;
  readonly recipientName: string;
  readonly signatureRef: string;
  readonly photoRef: string;
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly notes: string | null;
  readonly reviewStatus: PodReviewStatus;
  readonly reviewedBy: string | null;
  readonly reviewedAt: string | null;
  readonly capturedAt: string;
}

export interface ListPodsQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly reviewStatus?: PodReviewStatus;
}

export interface ListPodsResponse {
  readonly items: readonly PodRecord[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export async function listPodQueue(query: ListPodsQuery = {}): Promise<ListPodsResponse> {
  return request<ListPodsResponse>(baseUrl(), '/v1/pods/queue', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      reviewStatus: query.reviewStatus,
    },
  });
}

export async function getPod(id: string): Promise<PodRecord> {
  return request<PodRecord>(baseUrl(), `/v1/pods/${encodeURIComponent(id)}`, { method: 'GET' });
}

export async function reviewPod(
  id: string,
  decision: 'APPROVE' | 'REJECT',
  notes?: string,
): Promise<PodRecord> {
  const body = notes !== undefined ? { decision, notes } : { decision };
  return request<PodRecord>(baseUrl(), `/v1/pods/${encodeURIComponent(id)}/review`, {
    method: 'POST',
    body,
  });
}

// --------------------------------------------------------------------------
//  Delivery Dashboard (slice 2)
// --------------------------------------------------------------------------

export interface RouteStatusCounts {
  readonly DRAFT: number;
  readonly DISPATCHED: number;
  readonly IN_PROGRESS: number;
  readonly COMPLETED: number;
  readonly RECONCILED: number;
}

export interface DeliveryDashboard {
  readonly routesByStatus: RouteStatusCounts;
  readonly activeShifts: number;
  readonly podsPendingReview: number;
  readonly failedStopsToday: number;
  readonly pendingStops: number;
}

export async function getDeliveryDashboard(): Promise<DeliveryDashboard> {
  return request<DeliveryDashboard>(baseUrl(), '/v1/delivery/dashboard', { method: 'GET' });
}

/**
 * Daily KPI roll-up returned by `GET /v1/delivery/dashboards`. Distinct from
 * the dispatcher-state view above — see service-delivery
 * `dashboard.dto.ts > DailyDashboardResponseDto` for the documented gaps.
 */
export interface DeliveryDailyDashboard {
  readonly date: string;
  readonly deliveriesToday: number;
  readonly outForDelivery: number;
  readonly failedToday: number;
  readonly onTimeRate: number;
  readonly avgMinutesPerStop: number | null;
}

export async function getDeliveryDailyDashboard(): Promise<DeliveryDailyDashboard> {
  return request<DeliveryDailyDashboard>(baseUrl(), '/v1/delivery/dashboards', {
    method: 'GET',
  });
}

// --------------------------------------------------------------------------
//  Exceptions (slice 2)
// --------------------------------------------------------------------------

export type ExceptionResolution = 'REATTEMPT' | 'RETURN_TO_SENDER' | 'ESCALATE';

export interface ExceptionItem {
  readonly stopId: string;
  readonly routeId: string;
  readonly routeCode: string;
  readonly routeStatus: string;
  readonly sequence: number;
  readonly mailItemId: string | null;
  readonly address: unknown;
  readonly contactName: string | null;
  readonly contactPhone: string | null;
  readonly failureReason: string | null;
  readonly completedAt: string | null;
  readonly stopStatus: string;
}

export interface ListExceptionsQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly routeId?: string;
}

export interface ListExceptionsResponse {
  readonly items: readonly ExceptionItem[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface ExceptionResolveResult {
  readonly stopId: string;
  readonly stopStatus: string;
  readonly action: string;
  readonly notes: string | null;
}

export async function listExceptions(
  query: ListExceptionsQuery = {},
): Promise<ListExceptionsResponse> {
  return request<ListExceptionsResponse>(baseUrl(), '/v1/delivery/exceptions', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      routeId: query.routeId,
    },
  });
}

export async function resolveException(
  stopId: string,
  action: ExceptionResolution,
  notes?: string,
): Promise<ExceptionResolveResult> {
  const body: { action: string; notes?: string } = { action };
  if (notes !== undefined) body.notes = notes;
  return request<ExceptionResolveResult>(
    baseUrl(),
    `/v1/delivery/exceptions/${encodeURIComponent(stopId)}/resolve`,
    { method: 'POST', body },
  );
}

// --------------------------------------------------------------------------
//  Performance — operator analytics (deferred-removal wave)
// --------------------------------------------------------------------------

export interface PerformanceDriverRow {
  readonly driverId: string;
  readonly driverNumber: string;
  readonly fullName: string;
  readonly stopsAttempted: number;
  readonly stopsDelivered: number;
  readonly onTimeRate: number | null;
  readonly podAcceptanceRate: number | null;
  readonly avgMinutesPerStop: number | null;
}

export interface DeliveryPerformance {
  readonly periodDays: number;
  readonly onTimeRate: number;
  readonly podAcceptanceRate: number;
  readonly avgMinutesPerStop: number | null;
  readonly stopsAttempted: number;
  readonly stopsDelivered: number;
  readonly activeDrivers: number;
  readonly drivers: readonly PerformanceDriverRow[];
  readonly generatedAt: string;
}

export async function getDeliveryPerformance(periodDays = 30): Promise<DeliveryPerformance> {
  return request<DeliveryPerformance>(baseUrl(), '/v1/delivery/performance', {
    method: 'GET',
    query: { periodDays },
  });
}

// --------------------------------------------------------------------------
//  Auto-assign route (deferred-removal wave)
// --------------------------------------------------------------------------

export async function autoAssignRoute(routeId: string): Promise<DeliveryRoute & { chosenDriverId: string }> {
  return request<DeliveryRoute & { chosenDriverId: string }>(
    baseUrl(),
    `/v1/routes/${encodeURIComponent(routeId)}/auto-assign`,
    { method: 'POST', body: {} },
  );
}

// --------------------------------------------------------------------------
//  POD PDF (deferred-removal wave) — returns an object URL the caller revokes.
// --------------------------------------------------------------------------

export async function downloadPodPdf(podId: string): Promise<{ blob: Blob; filename: string }> {
  const url = `${baseUrl()}/v1/pods/${encodeURIComponent(podId)}/pdf`;
  const { getToken } = await import('../auth/tokenStore');
  const token = getToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`POD PDF failed: ${res.status}`);
  const blob = await res.blob();
  const filename = `pod-${podId}.pdf`;
  return { blob, filename };
}

// --------------------------------------------------------------------------
//  Vehicle maintenance log (deferred-removal wave)
// --------------------------------------------------------------------------

export type MaintenanceLogType = 'INSPECTION' | 'SERVICE' | 'REPAIR' | 'FUEL' | 'INCIDENT' | 'ODOMETER';

export interface MaintenanceLog {
  readonly id: string;
  readonly vehicleId: string;
  readonly logType: MaintenanceLogType;
  readonly notes: string | null;
  readonly odometerKm: number | null;
  /** DECIMAL string, NGN minor units (kobo). */
  readonly costMinor: string | null;
  readonly performedAt: string;
  readonly recordedBy: string;
  readonly createdAt: string;
}

export interface MaintenanceLogList {
  readonly items: readonly MaintenanceLog[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly lastOdometerKm: number | null;
  readonly lastServiceAt: string | null;
}

export async function listVehicleMaintenance(vehicleId: string): Promise<MaintenanceLogList> {
  return request<MaintenanceLogList>(
    baseUrl(),
    `/v1/vehicles/${encodeURIComponent(vehicleId)}/maintenance`,
    { method: 'GET' },
  );
}

export interface RecordMaintenanceRequest {
  readonly logType: MaintenanceLogType;
  readonly notes?: string;
  readonly odometerKm?: number;
  readonly costMinor?: string;
  readonly performedAt?: string;
}

export async function recordVehicleMaintenance(
  vehicleId: string,
  body: RecordMaintenanceRequest,
): Promise<MaintenanceLog> {
  return request<MaintenanceLog>(
    baseUrl(),
    `/v1/vehicles/${encodeURIComponent(vehicleId)}/maintenance`,
    { method: 'POST', body },
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GPS — fleet-wide active driver positions for the dispatcher dashboard.
// ─────────────────────────────────────────────────────────────────────────────

export interface ActiveDriverPosition {
  readonly id: string;
  readonly driverId: string;
  readonly shiftId: string;
  readonly routeId: string | null;
  readonly latitude: string;
  readonly longitude: string;
  readonly accuracyMeters: number | null;
  readonly speedKph: string | null;
  readonly recordedAt: string;
  readonly offRoute: boolean;
  readonly distanceFromPathMeters: string | null;
}

export interface ActivePositionsResponse {
  readonly items: readonly ActiveDriverPosition[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export async function listActiveDriverPositions(): Promise<ActivePositionsResponse> {
  return request<ActivePositionsResponse>(baseUrl(), '/v1/gps/active-positions', { method: 'GET' });
}
